import styled from "styled-components";
import { CSSVars } from "../../styles/styles.config";

// Started as a copy of widgetsLibraryV2.styles.ts (the previous /widgets page),
// restyled at the end of this file to the DEV-15438 "lavender option" board.
// Visual language borrowed from the live site, not the mockup: the /widgets and
// homepage hero (hero-bg.webp, light 300-weight title, pink to blue gradient
// phrase), the blog category pills, and the homepage Popular Widgets hover.
const brandGradient = "linear-gradient(90deg, #ff979c 0%, #8e90ff 100%)";
// The lavender board's page tint, card ink and accent.
const lavender = "#F5F5FF";
const ink = "#12172b";
const accent = "#6666FF";

export const StyledWidgetsCatalogWeb = styled.div`
  font-family: ${CSSVars.primaryFont};
  color: ${CSSVars.black};
  background: ${CSSVars.white};

  button {
    font-family: inherit;
    cursor: pointer;
  }

  .gradient {
    background: ${brandGradient};
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    white-space: nowrap;
  }

  /* Hero */
  .hero {
    position: relative;
    /* Above the sticky chips (10), or the search suggestions, which live
       inside this stacking context, slide under the chips and logos. */
    z-index: 11;
    color: ${CSSVars.white};
    text-align: center;
    background: url(/assets/hero-bg.webp);
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  /* Platform pages: the platform's own logo on a white tile, in place of
     the avatar pill, and a little closer to the headline. */
  .platform-badge {
    display: flex;
    justify-content: center;
    margin-bottom: 22px;
  }

  /* The platform icons in Mongo are white, drawn for a dark hero, so the
     tile is frosted glass rather than white. No border (Sergei, 09-29). */
  .platform-logo {
    position: relative;
    display: block;
    width: 72px;
    height: 72px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(6px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
    overflow: hidden;

    img {
      object-fit: contain;
      padding: 16px;
    }
  }

  /* The same icon, very large and faint, on the left of the hero: the old
     platform hero's watermark, turned down to match the main page marks. */
  .platform-watermark {
    position: absolute;
    left: -4%;
    top: 50%;
    width: min(46vw, 520px);
    aspect-ratio: 1;
    transform: translateY(-50%);
    opacity: 0.06;
    pointer-events: none;

    img {
      object-fit: contain;
    }

    @media (max-width: 767px) {
      opacity: 0.04;
    }
  }

  .hero-marks {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;

    svg {
      position: absolute;
      fill: ${CSSVars.white};
      opacity: 0.07;
    }

    /* Phones: the text spans the full width, so keep the marks fainter */
    @media (max-width: 767px) {
      svg {
        opacity: 0.045;
      }
    }
  }

  .hero-inner {
    position: relative;
    max-width: 1240px;
    margin: 0 auto;
    padding: 96px 20px 40px;

    @media screen and (min-width: ${CSSVars.breakpointLG}) {
      padding: 120px 40px 48px;
    }
  }

  .hero-proof {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;

    .rating-wrapper {
      width: auto;
      max-width: none;
    }

    p {
      color: ${CSSVars.white};
    }
  }

  h1 {
    margin: 0 auto;
    font-weight: 300;
    /* +2px on tablets and up (Sergei, 2026-09-29): 60px on laptops. Phones
       stay at 32px, where 34px pushes "zero code." onto a third line. */
    font-size: clamp(32px, calc(4.3vw + 2px), 60px);
    line-height: 1.12;
    letter-spacing: -0.02em;

    .gradient {
      font-weight: 400;
    }

    /* Always two lines: the promise, then the two proof points. */
    .second-line {
      display: block;
    }
  }

  .lead {
    max-width: 760px;
    margin: 20px auto 28px;
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 300;
    line-height: 1.6;
    opacity: 0.85;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSizeLG};
    }
  }

  .search {
    position: relative;
    max-width: 640px;
    margin: 0 auto;
    text-align: left;

    .search-icon {
      position: absolute;
      left: 20px;
      top: 29px;
      transform: translateY(-50%);
      font-size: 20px;
      color: ${CSSVars.gray500};
      pointer-events: none;
    }

    input {
      width: 100%;
      height: 58px;
      padding: 0 120px 0 52px;
      border: 1px solid transparent;
      border-radius: 999px;
      outline: none;
      font-family: inherit;
      font-size: 16px;
      color: ${CSSVars.black};
      background: ${CSSVars.white};
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.3);

      &:focus-visible {
        border-color: ${CSSVars.blue500};
        box-shadow: 0 0 0 4px rgba(91, 91, 255, 0.35), 0 16px 40px rgba(0, 0, 0, 0.3);
      }

      &::placeholder {
        color: ${CSSVars.gray500};
        font-weight: 300;
      }

      @media (max-width: 480px) {
        padding-right: 20px;
        font-size: 15px;
      }
    }

    > button {
      position: absolute;
      right: 6px;
      top: 6px;
      height: 46px;
      padding: 0 24px;
      border: 0;
      border-radius: 999px;
      color: ${CSSVars.white};
      background: ${CSSVars.blue500};
      font-size: ${CSSVars.fontSizeSM};
      font-weight: 500;
      transition: background 0.2s;

      &:hover {
        background: linear-gradient(270deg, #ff979c 0%, ${CSSVars.blue500} 100%);
      }

      @media (max-width: 480px) {
        display: none;
      }
    }
  }

  .suggest-panel {
    position: absolute;
    z-index: 20;
    left: 0;
    right: 0;
    top: 66px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 18px;
    background: ${CSSVars.white};
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
    text-align: left;
  }

  .suggest-all {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px;
    border: 0;
    border-top: 1px solid ${CSSVars.gray100};
    background: ${CSSVars.white};
    color: ${CSSVars.blue500};
    font-family: inherit;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 500;
    cursor: pointer;

    &:hover {
      background: ${CSSVars.blue50};
    }
  }

  .suggestions {
    /* About five rows, then the list scrolls on its own */
    max-height: min(300px, 45vh);
    overflow-y: auto;
    overscroll-behavior: contain;
    margin: 0;
    padding: 8px;
    list-style: none;

    a {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 12px;
      border-radius: 12px;
      color: ${CSSVars.black};

      img {
        border-radius: 7px;
      }

      strong {
        display: block;
        font-size: ${CSSVars.fontSizeSM};
        font-weight: 500;
      }

      small {
        display: block;
        color: ${CSSVars.gray500};
        font-size: ${CSSVars.fontSizeXS};
        font-weight: 300;
      }
    }

    li[aria-selected="true"] a,
    a:hover {
      background: ${CSSVars.blue50};
    }
  }

  .quick {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 6px 14px;
    margin-top: 16px;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 300;

    > span {
      opacity: 0.6;
    }

    button {
      padding: 0;
      border: 0;
      background: transparent;
      color: ${CSSVars.white};
      font-size: inherit;
      font-weight: 400;
      text-decoration: underline;
      text-decoration-color: rgba(255, 255, 255, 0.35);
      text-underline-offset: 4px;

      &:hover {
        text-decoration-color: #ff979c;
      }
    }
  }

  .scores {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 6px 10px;
    margin-top: 26px;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 300;

    .star {
      color: #ffc24d;
    }

    strong {
      font-weight: 600;
    }

    .dot {
      opacity: 0.5;
    }
  }

  /* Category chips: real links, sticky while you browse */
  .chips {
    position: sticky;
    top: 0;
    z-index: 10;
    /* Near-solid instead of a backdrop blur: re-blurring every card that
       scrolls under a sticky bar costs a repaint per frame. */
    background: rgba(255, 255, 255, 0.97);
    border-bottom: 1px solid ${CSSVars.gray100};
    /* top is set inline to sit under the floating site header; it moves
       with the header's own 0.3s slide. */
    transition: top 0.3s ease, background 0.25s ease, box-shadow 0.25s ease;

    /* Once it floats over the cards: a soft lavender tint, a shadow and a
       brand-gradient hairline, so it reads as a bar and not as more content.
       The pills turn solid white to stand off the tint. */
    &.is-stuck {
      background: rgba(247, 246, 255, 0.98);
      border-bottom-color: transparent;
      box-shadow: 0 8px 24px rgba(76, 79, 255, 0.12);

      &:after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 2px;
        background: ${brandGradient};
        opacity: 0.7;
      }

      .chip:not(.active) {
        border-color: #e3e3f5;
        background: ${CSSVars.white};
        box-shadow: 0 1px 3px rgba(22, 22, 42, 0.06);
      }
    }
  }

  .chips-track {
    display: flex;
    gap: 6px;
    max-width: 1440px;
    margin: 0 auto;
    padding: 14px 20px;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    @media screen and (min-width: ${CSSVars.breakpointXL}) {
      flex-wrap: wrap;
      justify-content: center;
      padding: 16px 24px;
    }
  }

  .chip {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px 6px 9px;
    border-radius: 999px;
    border: 1px solid ${CSSVars.gray100};
    color: ${CSSVars.gray600};
    background: ${CSSVars.white};
    /* Nine chips fit one row from about 1300px wide */
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    transition: 0.2s;

    /* Design B: a line icon in the brand lavender, and the brand gradient
       on the selected chip. No counts, so small groups do not look thin. */
    svg {
      flex-shrink: 0;
      width: 15px;
      height: 15px;
      color: #8e90ff;
      transition: color 0.2s;
    }

    &:hover {
      border-color: #8e90ff;
      color: ${CSSVars.black};
    }

    &.active {
      color: ${CSSVars.white};
      background: ${brandGradient} border-box;
      border-color: transparent;
      box-shadow: 0 4px 14px rgba(142, 144, 255, 0.3);

      svg {
        color: ${CSSVars.white};
      }
    }
  }

  /* Main */
  .main {
    max-width: 1280px;
    margin: 0 auto;
    padding: 40px 20px 60px;

    @media screen and (min-width: ${CSSVars.breakpointLG}) {
      padding: 56px 40px 80px;
    }
  }

  .row + .row {
    margin-top: 56px;
  }

  #all-widgets {
    scroll-margin-top: 80px;
  }

  .row-head {
    margin-bottom: 22px;

    h2 {
      margin: 0 0 6px;
      font-size: ${CSSVars.fontSizeXL};
      font-weight: 600;
      line-height: 1.25;

      @media screen and (min-width: ${CSSVars.breakpointLG}) {
        font-size: ${CSSVars.fontSize2XL};
      }
    }

    p {
      margin: 0;
      color: ${CSSVars.gray500};
      font-size: ${CSSVars.fontSizeSM};
      font-weight: 300;
    }
  }

  .row-head-split {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
  }

  .clear {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    padding: 7px 14px;
    border: 1px solid ${CSSVars.gray100};
    border-radius: 999px;
    color: ${CSSVars.black};
    background: ${CSSVars.white};
    font-size: ${CSSVars.fontSizeSM};

    &:hover {
      border-color: ${CSSVars.blue500};
      color: ${CSSVars.blue500};
    }
  }

  .grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;

    @media screen and (min-width: ${CSSVars.breakpointSM}) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (min-width: ${CSSVars.breakpointLG}) {
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
    }

    @media screen and (min-width: ${CSSVars.breakpointXL}) {
      grid-template-columns: repeat(4, 1fr);
    }

    /* Cards past the current page stay in the HTML as crawlable links. */
    > .is-hidden {
      display: none;
    }

    > div {
      display: flex;
    }
  }

  /* Card: keeps the illustrated catalog previews, our edge over icon-only grids */
  .card {
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden;
    border: 1px solid ${CSSVars.gray100};
    border-radius: 16px;
    color: ${CSSVars.black};
    background: ${CSSVars.white};
    transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      border-color: ${CSSVars.blue500};
      transform: translateY(-3px);
      box-shadow: 0 10px 28px rgba(76, 79, 255, 0.14);

      .card-preview img {
        transform: scale(1.03);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;

      &:hover {
        transform: none;

        .card-preview img {
          transform: none;
        }
      }
    }
  }

  .card-preview {
    position: relative;
    display: block;
    aspect-ratio: 320 / 170;
    overflow: hidden;
    background: ${CSSVars.gray50};

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }
  }

  .badge {
    position: absolute;
    top: 10px;
    left: 10px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 8px;
    border-radius: 999px;
    color: ${CSSVars.black};
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 2px 8px rgba(1, 1, 1, 0.12);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.01em;

    &:before {
      content: "";
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: ${CSSVars.pink500};
    }

    &.badge-trending:before {
      background: ${CSSVars.blue500};
    }

    /* New stands apart from Popular and Trending: a filled brand-gradient
       pill with white text, instead of a white pill with a dot. */
    &.badge-new {
      padding: 4px 11px;
      color: ${CSSVars.white};
      background: linear-gradient(90deg, #ff7f86 0%, #7b7dff 100%);
      box-shadow: 0 3px 10px rgba(123, 125, 255, 0.35);
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;

      &:before {
        content: "✦";
        width: auto;
        height: auto;
        border-radius: 0;
        background: none;
        font-size: 10px;
      }
    }
  }

  /* A gradient hairline around the whole card, so a new widget is spotted
     in the grid as well as in the Recently added row. */
  .card.card-new {
    border-color: transparent;
    background:
      linear-gradient(${CSSVars.white}, ${CSSVars.white}) padding-box,
      ${brandGradient} border-box;
  }

  .card-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px 18px 18px;
  }

  .card-title {
    display: flex;
    align-items: center;
    gap: 10px;

    img {
      flex-shrink: 0;
      border-radius: 7px;
    }

    strong {
      display: block;
      font-size: 15px;

      .for {
        font-weight: 400;
        color: ${CSSVars.gray500};
      }

      font-weight: 500;
      line-height: 1.3;
    }

    small {
      display: block;
      color: ${CSSVars.gray500};
      font-size: 12px;
      font-weight: 300;
    }
  }

  .card-desc {
    color: ${CSSVars.gray600};
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 300;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .more {
    display: flex;
    justify-content: center;
    margin-top: 32px;

    button {
      padding: 13px 30px;
      border: 1px solid ${CSSVars.black};
      border-radius: 999px;
      color: ${CSSVars.black};
      background: ${CSSVars.white};
      font-size: ${CSSVars.fontSizeSM};
      font-weight: 500;
      transition: 0.2s;

      &:hover {
        color: ${CSSVars.white};
        background: ${CSSVars.black};
      }
    }
  }

  .empty {
    padding: 36px 20px;
    border: 1px dashed ${CSSVars.gray200};
    border-radius: 16px;
    text-align: center;

    h3 {
      margin: 0 0 6px;
      font-size: ${CSSVars.fontSizeXL};
      font-weight: 600;
    }

    > p {
      margin: 0 0 18px;
      color: ${CSSVars.gray500};
      font-weight: 300;
    }

    .grid {
      margin-top: 24px;
      text-align: left;
    }
  }

  .empty-chips {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
  }

  .empty-embd {
    margin: 24px 0 0 !important;

    a {
      color: ${CSSVars.blue500};
      font-weight: 500;
    }
  }

  .platform-intro {
    max-width: 820px;
    margin: 72px auto 0;
    text-align: center;

    h2 {
      margin: 0 0 14px;
      font-size: clamp(24px, 3vw, 34px);
      font-weight: 300;
    }

    p {
      margin: 0;
      color: ${CSSVars.gray600};
      font-weight: 300;
      line-height: 1.7;
      white-space: pre-line;
    }
  }

  /* How it works: a light band in the brand pink and blue tints */
  .steps {
    margin: 80px -20px 0;
    padding: 56px 20px;
    text-align: center;
    background: linear-gradient(120deg, ${CSSVars.pink50} 0%, ${CSSVars.blue50} 100%);

    @media screen and (min-width: ${CSSVars.breakpointLG}) {
      margin: 96px 0 0;
      padding: 64px 48px;
      border-radius: 24px;
    }

    h2 {
      margin: 0 0 32px;
      font-size: clamp(28px, 4vw, 44px);
      font-weight: 300;
    }

    ol {
      display: grid;
      grid-template-columns: 1fr;
      gap: 16px;
      max-width: 1000px;
      margin: 0 auto;
      padding: 0;
      list-style: none;
      text-align: left;

      @media screen and (min-width: ${CSSVars.breakpointMD}) {
        grid-template-columns: repeat(3, 1fr);
      }
    }

    li {
      padding: 22px;
      border: 1px solid rgba(255, 255, 255, 0.9);
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.75);
      box-shadow: 0 6px 20px rgba(76, 79, 255, 0.06);

      strong {
        display: block;
        margin: 14px 0 6px;
        font-size: ${CSSVars.fontSizeLG};
        font-weight: 500;
      }

      p {
        margin: 0;
        color: ${CSSVars.gray600};
        font-size: ${CSSVars.fontSizeSM};
        font-weight: 300;
        line-height: 1.6;
      }
    }
  }

  .step-num {
    display: inline-grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    color: ${CSSVars.white};
    background: linear-gradient(135deg, #ff979c 0%, ${CSSVars.blue500} 100%);
    font-weight: 600;
  }

  .steps-cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 32px;
    padding: 14px 32px;
    border-radius: 999px;
    color: ${CSSVars.white};
    background: ${CSSVars.blue500};
    font-weight: 500;
    transition: 0.2s;

    &:hover {
      background: linear-gradient(270deg, #ff979c 0%, ${CSSVars.blue500} 100%);
    }
  }

  .steps-note {
    margin: 12px 0 0;
    color: ${CSSVars.gray500};
    font-size: 12px;
    font-weight: 300;
  }

  .faq-wrap {
    max-width: 900px;
    margin: 40px auto 0;
  }

  /* Phones: curated rows swipe sideways, the full list goes two-up and
     compact, so the page is not one very long single column. */
  @media (max-width: 767px) {
    .grid-scroll {
      display: flex;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      gap: 12px;
      margin: 0 -20px;
      padding: 2px 20px 10px;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }

      > .card {
        flex: 0 0 74%;
        max-width: 300px;
        scroll-snap-align: start;
      }
    }

    .grid-compact {
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;

      .card-body {
        padding: 10px 12px 12px;
      }

      .card-title {
        gap: 8px;

        img {
          width: 22px;
          height: 22px;
        }

        strong {
          font-size: 13px;
        }

        small {
          display: none;
        }
      }

      .card-desc {
        display: none;
      }

      .badge {
        top: 6px;
        left: 6px;
        padding: 2px 8px;
        font-size: 10px;
      }
    }
  }

  /* ===== DEV-15438 "Widget Catalog: lavender option" board ===== */
  background: ${lavender};

  .chips {
    background: rgba(245, 245, 255, 0.97);
    border-bottom-color: #e6e6f5;
  }

  /* Sticky filter bar: a search field joins the chips once the bar sticks. */
  .chips-search {
    display: none;
    position: relative;
    flex: 0 0 280px;
    margin: 0 8px 0 0;

    svg {
      position: absolute;
      left: 16px;
      top: 50%;
      width: 18px;
      height: 18px;
      transform: translateY(-50%);
      color: #3d4049;
      pointer-events: none;
    }

    input {
      width: 100%;
      height: 40px;
      box-sizing: border-box;
      margin: 0;
      padding: 0 16px 0 44px;
      border: 1px solid ${CSSVars.gray100};
      border-radius: 999px;
      background: ${CSSVars.white};
      font-family: inherit;
      font-size: 14px;
      color: ${CSSVars.black};
      outline: none;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;

      &::placeholder {
        color: ${CSSVars.gray400};
      }

      &:focus {
        border-color: #8e90ff;
        box-shadow: 0 0 0 3px rgba(142, 144, 255, 0.2);
      }
    }

    @media (max-width: 767px) {
      flex-basis: 200px;
    }
  }

  .chips.is-stuck {
    .chips-search {
      display: block;
    }

    &:after {
      display: none;
    }

    .chips-track {
      flex-wrap: nowrap;
      justify-content: flex-start;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      overflow-x: auto;
    }
  }

  /* Widget cards: white tile with the widget's hero shot, title and
     description underneath, rotating gradient outline on hover. */
  .cg-grid {
    display: grid;
    grid-template-columns: 1fr;
    column-gap: 32px;
    row-gap: 36px;
    margin-top: 8px;

    @media screen and (min-width: 641px) {
      grid-template-columns: repeat(2, 1fr);
      row-gap: 48px;
    }

    @media screen and (min-width: 1001px) {
      grid-template-columns: repeat(3, 1fr);
    }

    > .is-hidden {
      display: none;
    }
  }

  @property --cg-angle {
    syntax: "<angle>";
    initial-value: 135deg;
    inherits: false;
  }

  @keyframes cg-spin {
    to {
      --cg-angle: 495deg;
    }
  }

  .cg-card {
    display: flex;
    flex-direction: column;
    text-decoration: none;
    color: ${ink};

    &:focus-visible {
      outline: none;
    }

    &:hover .cg-title {
      color: ${accent};
    }

    &:hover .cg-media {
      transform: translateY(-2px);
      box-shadow: 0 2px 4px rgba(18, 23, 43, 0.04),
        0 16px 40px rgba(102, 102, 255, 0.16);
    }

    &:hover .cg-media::before,
    &:focus-visible .cg-media::before {
      opacity: 1;
      animation: cg-spin 3s linear infinite;
    }
  }

  .cg-media {
    position: relative;
    isolation: isolate;
    aspect-ratio: 4 / 3;
    padding: 16px;
    display: grid;
    place-items: center;
    overflow: hidden;
    background: ${CSSVars.white};
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(18, 23, 43, 0.04),
      0 8px 24px rgba(102, 102, 255, 0.06);
    transition: box-shadow 0.3s ease, transform 0.3s ease;

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      z-index: 2;
      padding: 2px;
      border-radius: inherit;
      pointer-events: none;
      background: linear-gradient(
        var(--cg-angle),
        #6666ff 0%,
        #9876de 50%,
        #ff979c 100%
      );
      -webkit-mask: linear-gradient(#000 0 0) content-box,
        linear-gradient(#000 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      filter: drop-shadow(0 12px 20px rgba(18, 23, 43, 0.14));
    }

    /* Wide hub heroes carry wide transparent margins; enlarge them so their
       visual weight matches the 4:3 hero shots. */
    &.cg-fallback {
      padding: 8px 0;

      img {
        width: 125%;
        max-width: none;
        height: auto;
      }
    }
  }

  .cg-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 1;
    padding: 6px 9px;
    border-radius: 4px;
    color: ${accent};
    background: ${lavender};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1;

    &[data-kind="New"] {
      color: ${CSSVars.white};
      background: ${accent};
    }
  }

  .cg-body {
    padding: 16px 2px 0;
  }

  .cg-title {
    margin: 0;
    font-size: 20px;
    line-height: 1.3;
    font-weight: 600;
    letter-spacing: -0.015em;
    transition: color 0.2s ease;
  }

  .cg-desc {
    margin: 8px 0 0;
    color: #545c76;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.55;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  @media (prefers-reduced-motion: reduce) {
    .cg-media,
    .cg-media::before,
    .cg-title {
      transition: none;
    }

    .cg-card:hover .cg-media {
      transform: none;
    }

    .cg-card:hover .cg-media::before,
    .cg-card:focus-visible .cg-media::before {
      animation: none;
    }
  }

  /* Highlight banners: full-width dark spotlights between card rows. */
  .hl-slot {
    grid-column: 1 / -1;
    --hl-night: #0b0d1f;
    --hl-violet: #6a5cff;
    --hl-pink: #f08bb4;
    --hl-lilac: #b9a8ff;
    --hl-on: #ffffff;
    --hl-on-muted: rgba(255, 255, 255, 0.72);
    --hl-line: rgba(255, 255, 255, 0.12);
    --hl-h: 380px;
    font-family: ${CSSVars.secondaryFont};

    &[data-slot="2"] {
      --hl-aurora: conic-gradient(from 200deg, #6a5cff, #f08bb4, #38bdf8, #6a5cff);
    }

    &[data-slot="4"] {
      --hl-aurora: conic-gradient(from 200deg, #34d399, #38bdf8, #6a5cff, #34d399);
    }

    h2,
    .hl-num,
    .hl-btn,
    .hl-eyebrow,
    .hl-tag {
      font-family: ${CSSVars.primaryFont};
    }
  }

  .hl-a,
  .hl-d {
    position: relative;
    display: grid;
    align-items: center;
    gap: 32px;
    height: var(--hl-h);
    padding: 0 0 0 56px;
    overflow: hidden;
    border-radius: 20px;
    color: var(--hl-on);
  }

  .hl-a {
    grid-template-columns: 1fr 1.1fr;
    background: radial-gradient(60% 80% at 85% 40%, rgba(106, 92, 255, 0.45), transparent 70%),
      radial-gradient(40% 60% at 100% 100%, rgba(240, 139, 180, 0.28), transparent 70%),
      var(--hl-night);

    .hl-art img {
      height: 118%;
      width: auto;
      max-width: none;
      filter: drop-shadow(0 30px 50px rgba(0, 0, 0, 0.45));
    }
  }

  .hl-d {
    grid-template-columns: 1.15fr 0.85fr;
    background: var(--hl-night);

    &::after {
      content: "";
      position: absolute;
      width: 760px;
      height: 760px;
      right: -220px;
      top: -180px;
      border-radius: 50%;
      pointer-events: none;
      background: var(--hl-aurora, conic-gradient(from 200deg, #4285f4, #34a853, #fbbc05, #ea4335, #4285f4));
      filter: blur(110px);
      opacity: 0.32;
      /* Own compositor layer: the 110px blur is drawn once, not again on
         every scroll frame or hover. */
      will-change: transform;
      transform: translateZ(0);
    }

    > * {
      position: relative;
      z-index: 1;
    }

    .hl-name {
      margin-top: 22px;
    }

    .hl-art {
      perspective: 1400px;
      overflow: visible;
      justify-content: flex-start;

      img {
        height: 112%;
        width: auto;
        max-width: none;
        transform: rotateY(-14deg) rotateX(6deg);
        transform-origin: left center;
        filter: drop-shadow(0 40px 60px rgba(0, 0, 0, 0.55));
        transition: transform 0.6s cubic-bezier(0.2, 0.7, 0.2, 1);
      }
    }

    &:hover .hl-art img {
      transform: rotateY(-6deg) rotateX(3deg);
    }
  }

  .hl-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 12px;
    line-height: 1;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--hl-lilac);
  }

  .hl-grad {
    background: linear-gradient(90deg, var(--hl-pink), var(--hl-lilac));
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    font-weight: 600;
  }

  .hl-name {
    margin: 14px 0 10px;
    color: var(--hl-on);
    font-size: 46px;
    line-height: 1.05;
    letter-spacing: -0.03em;
    font-weight: 700;
    white-space: nowrap;
  }

  .hl-tag {
    margin: 0 0 26px;
    max-width: 480px;
    font-size: 20px;
    font-weight: 500;
    line-height: 1.4;
    color: var(--hl-on-muted);
  }

  .hl-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .hl-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    height: 46px;
    padding: 0 22px;
    border-radius: 12px;
    font-weight: 600;
    font-size: 15px;
    line-height: 1;
    text-decoration: none;
    transition: transform 0.2s ease, background-color 0.2s ease;

    svg {
      width: 18px;
      height: 18px;
    }

    &:focus-visible {
      outline: 3px solid var(--hl-lilac);
      outline-offset: 3px;
    }
  }

  .hl-btn-primary {
    background: var(--hl-violet);
    color: ${CSSVars.white};
    box-shadow: 0 10px 30px -10px rgba(106, 92, 255, 0.8);

    &:hover {
      background: #7a6dff;
      color: ${CSSVars.white};
      transform: translateY(-1px);
    }
  }

  .hl-btn-ghost {
    color: var(--hl-on);
    border: 1px solid var(--hl-line);

    &:hover {
      background: rgba(255, 255, 255, 0.06);
      color: var(--hl-on);
    }
  }

  .hl-proof {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 22px;
    margin-top: 20px;
    color: var(--hl-on-muted);
    font-size: 14px;

    strong {
      color: var(--hl-on);
      font-weight: 600;
    }
  }

  .hl-stars {
    color: #fbbc05;
    letter-spacing: 1px;
  }

  .hl-dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--hl-line);
  }

  .hl-art {
    height: var(--hl-h);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .hl-score {
    display: flex;
    align-items: flex-end;
    gap: 16px;
  }

  .hl-num {
    font-weight: 700;
    font-size: 84px;
    line-height: 0.85;
    letter-spacing: -0.05em;
  }

  .hl-meta {
    padding-bottom: 4px;

    small {
      color: var(--hl-on-muted);
      font-size: 13px;
    }
  }

  @media (max-width: 900px) {
    .hl-a,
    .hl-d {
      grid-template-columns: 1fr;
      height: auto;
      padding: 40px 24px;
    }

    .hl-art {
      height: auto;
    }

    .hl-a .hl-art img,
    .hl-d .hl-art img {
      width: 100%;
      height: auto;
    }

    .hl-name {
      font-size: 34px;
      white-space: normal;
    }

    .hl-num {
      font-size: 64px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .hl-btn,
    .hl-d .hl-art img {
      transition: none;
    }
  }

  /* The empty state reuses the catalog cards. */
  .empty .cg-grid {
    margin-top: 24px;
    text-align: left;
  }
`;
